import { Button, TableCell, TableRow } from '@heroui/react';
import React from 'react';

import { BaseModal } from '../../../../../../../components/BaseModal';
import {
  LoadingTable,
  type LoadingTableColumn,
} from '../../../../../../../components/Table';
import { type PromotionRow, type PromotionTier } from '../types';
import { ABSENT_VALUE } from '../utils/vendorPromotionMapper';

import { PromotionDisplayCopyForm } from './PromotionDisplayCopyForm';

interface PromotionDetailModalProps {
  /** The selected row, or null when the modal is closed. */
  promotion: PromotionRow | null;
  vendorId?: string;
  onClose: () => void;
  /** Hands the saved record back so the list row can be replaced. */
  onSaved: (promotion: PromotionRow) => void;
}

const tierColumns: LoadingTableColumn[] = [
  { label: 'Tier', className: 'pl-6 min-w-32' },
  { label: 'Conditions', className: 'min-w-48' },
  { label: 'Offers', className: 'min-w-40' },
  { label: 'Max applications', className: 'min-w-32' },
];

/** A list of one tier's conditions or offers, or an en dash where it has none. */
const TierCell: React.FC<{ lines: string[] }> = ({ lines }) =>
  lines.length > 0 ? (
    <ul className="space-y-1">
      {lines.map((line, index) => (
        <li key={index} className="text-sm text-default-600">
          {line}
        </li>
      ))}
    </ul>
  ) : (
    <span className="text-sm text-default-400">{ABSENT_VALUE}</span>
  );

const renderTierRow = (tier: PromotionTier): React.ReactNode => (
  <TableRow key={tier.id}>
    <TableCell className="pl-6 align-top">
      <span className="font-medium text-foreground">{tier.name}</span>
    </TableCell>
    <TableCell className="align-top">
      <TierCell
        lines={tier.conditions.map(
          (condition) =>
            `${condition.metricLabel}: min ${condition.minimum}, max ${condition.maximum}`
        )}
      />
    </TableCell>
    <TableCell className="align-top">
      <TierCell
        lines={tier.offers.map((offer) => `${offer.typeLabel}: ${offer.value}`)}
      />
    </TableCell>
    <TableCell className="align-top text-sm tabular-nums text-default-600">
      {tier.maxApplications ?? ABSENT_VALUE}
    </TableCell>
  </TableRow>
);

export const PromotionDetailModal: React.FC<PromotionDetailModalProps> = ({
  promotion,
  vendorId,
  onClose,
  onSaved,
}) => (
  <BaseModal
    isOpen={promotion !== null}
    onClose={onClose}
    size="3xl"
    title={promotion?.name ?? 'Promotion'}
    subtitle={promotion?.code}
    scrollBehavior="inside"
    footer={
      <div className="flex w-full items-center justify-between">
        <span className="text-xs text-default-400">
          Promotions are set up by the distributor. The display copy can be
          overridden here.
        </span>
        <Button variant="flat" onPress={onClose}>
          Close
        </Button>
      </div>
    }
  >
    {promotion && (
      <div className="space-y-6">
        <div className="space-y-2">
          {promotion.shortDescription ? (
            <p className="text-sm font-medium text-foreground">
              {promotion.shortDescription}
            </p>
          ) : (
            <p className="text-sm italic text-default-400">
              No short description
            </p>
          )}
          {promotion.description ? (
            <p className="text-sm text-default-600">{promotion.description}</p>
          ) : (
            <p className="text-sm italic text-default-400">No description</p>
          )}
        </div>

        <div>
          <h4 className="mb-2 text-sm font-semibold text-foreground">
            Tiers ({promotion.tierCount})
          </h4>
          {promotion.tiers.length > 0 ? (
            <LoadingTable<PromotionTier>
              ariaLabel="Promotion tiers"
              columns={tierColumns}
              data={promotion.tiers}
              renderRow={renderTierRow}
            />
          ) : (
            <p className="text-sm text-default-400">
              This promotion has no tiers.
            </p>
          )}
        </div>

        <PromotionDisplayCopyForm
          key={promotion.id}
          promotion={promotion}
          vendorId={vendorId}
          onSaved={onSaved}
        />
      </div>
    )}
  </BaseModal>
);
